// Grid helpers (IMPLEMENTATION §2.1 core/grid). World coordinates (x, y) are 0-based on the fixed 7×9 grid;
// y grows downward, so N is y−1. Adjacency is strictly N/E/S/W, and that order is the canonical scan order
// (deposit output round-robin, BFS neighbours).
import type { Cell, Dir, Int } from './types.ts';

export const DIRS: readonly Dir[] = ['N', 'E', 'S', 'W'];
const DX: readonly Int[] = [0, 1, 0, -1];
const DY: readonly Int[] = [-1, 0, 1, 0];

/** Inclusive rectangle (an expansion mask). */
export interface Rect {
  x0: Int;
  y0: Int;
  x1: Int;
  y1: Int;
}

export function dirIndex(d: Dir): Int {
  return d === 'N' ? 0 : d === 'E' ? 1 : d === 'S' ? 2 : 3;
}

/** Direction by index, wrapping (dirAt(5) = 'E'). */
export function dirAt(i: Int): Dir {
  return DIRS[((i % 4) + 4) % 4] as Dir;
}

export function dxOf(d: Dir): Int {
  return DX[dirIndex(d)] as Int;
}

export function dyOf(d: Dir): Int {
  return DY[dirIndex(d)] as Int;
}

export function opposite(d: Dir): Dir {
  return dirAt(dirIndex(d) + 2);
}

export function rotateCw(d: Dir): Dir {
  return dirAt(dirIndex(d) + 1);
}

/** The neighbouring cell in direction d. */
export function stepCell(x: Int, y: Int, d: Dir): Cell {
  return { x: x + dxOf(d), y: y + dyOf(d) };
}

/** Direction from a to an adjacent b, or null when they are not 4-neighbours. */
export function dirBetween(ax: Int, ay: Int, bx: Int, by: Int): Dir | null {
  for (let i = 0; i < 4; i++) {
    if (ax + (DX[i] as Int) === bx && ay + (DY[i] as Int) === by) return DIRS[i] as Dir;
  }
  return null;
}

/** True for integer coordinates inside cols × rows. */
export function inGrid(cols: Int, rows: Int, x: Int, y: Int): boolean {
  return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < cols && y < rows;
}

export function cellIndex(cols: Int, x: Int, y: Int): Int {
  return y * cols + x;
}

export function inRect(r: Rect, x: Int, y: Int): boolean {
  return x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
}
